import React, {useContext, useState} from 'react';
import {Select, Input, Form} from "antd";
import {DIALOG_KEY} from "../../../shared/utils/enum";
import toast from "react-hot-toast";
import duration from 'dayjs/plugin/duration';
import dayjs from "dayjs";
import {CancelButton, CopyButton, DeleteButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import DateStepper from "../../../shared/components/date/DateStepper";
import CustomDateTimePicker from "../../../shared/components/date/CustomDateTimePicker";
import {AuthContext} from "../../../shared/contexts/authContext";
import {useShopPackage, useShopPackageGroup} from "../../probability/useItemData";
import {useDeleteCharacterPackage, useMiniGame, useSaveCharacterPackage} from "./useCharacterPackage";
import {CharacterPackageEntity} from "./CharacterPackageEntity";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import {useMiniGameData} from "../../logs/useTableData";

dayjs.extend(duration);

const getNameList = (target, index, shopPackageGroup, shopPackage) => {
    return Object.values(shopPackageGroup[target])
        .filter((group) => group.groupIdx === shopPackage[target][index]?.groupIdx)
        .map((group, index) => ({
            ...group,
            key: group.groupIdx + index,
        }));
}
const getMiniGameIndexList = (target, miniGame) => {
    return miniGame.map((game) => ({
        ...game,
        key: game.index,
    }));
}

// const intervalUnitOptions = [
//     {label: '시간', value: 'hour'},
//     {label: '분', value: 'minute'},
//     {label: '일', value: 'day'},
// ];

const CharacterPackageEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const {currentUser} = useContext(AuthContext);
    const {data: shopPackage, isLoading: isShopPackageLoading, error: shopPackageError} = useShopPackage();
    const {
        data: shopPackageGroup,
        isLoading: isShopPackageGroupLoading,
        error: shopPackageGroupError
    } = useShopPackageGroup();
    const {data: miniGame, isLoading: isMiniGameLoading, error: miniGameError} = useMiniGame();

    const {mutate: saveCharacterPackage, isPending: isSavePending} = useSaveCharacterPackage();
    const {mutate: deleteCharacterPackage, isPending: isDeletePending} = useDeleteCharacterPackage();

    const isLoading = isShopPackageLoading || isShopPackageGroupLoading || isMiniGameLoading;
    const error = shopPackageError || shopPackageGroupError || miniGameError;
    const isPending = isSavePending || isDeletePending;

    const [row, setRow] = useState(new CharacterPackageEntity({
        ...record,
        intervalValue: record.intervalValue || 1,
        intervalUnit: record.intervalUnit || 'hour',
    }));

    const handleSetRows = (key, value) => {
        setRow((prevState) => {
            const newState = {...prevState};
            newState[key] = value;
            return new CharacterPackageEntity(newState);
        });
    }

    const saveRowData = async (rowData) => {
        await saveCharacterPackage(rowData, {
            onSuccess: (res) => {
                toast.dismiss();
                toast.success('저장 성공');
                handleClose(true);
            },
            onError: (err) => {
                toast.dismiss();
                console.error(err);
                toast.error("저장 실패");
            }
        });
    }

    const handleSave = async (e) => {
        e.preventDefault();
        if (row.isInvalidate()) return;

        await saveRowData(row.toJson());
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    const handleCopy = async (e) => {
        e.preventDefault();
        if (row.isInvalidate()) return;
        row.seq = 0; // 복사 시 seq 0으로 설정

        await saveRowData(row.toJson());
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    const handleDelete = async (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;  // ref에서 최신 상태 가져오기
        if (currentDialog.confirmText === 'DELETE') {

            await deleteCharacterPackage(row.seq, {
                onSuccess: (res) => {
                    toast.dismiss();
                    toast.success('삭제 성공');
                    handleClose(true);
                },
                onError: (err) => {
                    toast.dismiss();
                    console.error(err);
                    toast.error("삭제 실패");
                }
            });
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    }

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;
    if (isPending) {
        toast.loading('저장 중...');
    }

    const nameList = getNameList(currentUser.target, record.index, shopPackageGroup, shopPackage);
    const miniGameIndexList = getMiniGameIndexList(currentUser.target, miniGame);

    return (
        <div>
            <div style={{marginTop: 26}}>
                <Form layout="inline">
                    <Form.Item label="패키지 이름" style={{marginBottom: 8, width: '35%'}}>
                        <Input
                            name={"packageName"}
                            value={row.packageName}
                            onChange={(e) => handleSetRows('packageName', e.target.value)}
                            size="small"
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="타이틀" style={{width: '35%', marginLeft: '1%'}}>
                        <Select
                            onChange={(e) => handleSetRows('packageName', e)}
                            size="small"
                            className="name-select"
                            showSearch
                            optionLabelProp="label"
                            value={row.packageName}
                        >
                            {nameList.map((option, index) => (
                                <Select.Option
                                    key={index}
                                    value={option.nameText}
                                    label={option.nameText}
                                >
                                    <div>
                                        <div style={{fontSize: '11px', color: '#999'}}>
                                            ID: {option.index}
                                        </div>
                                        <div style={{fontWeight: 'bold'}}>
                                            {option.nameText}
                                        </div>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="OnOff" style={{marginBottom: 8, marginLeft: 8, width: '15%'}}>
                        <Select
                            value={row.onOff}
                            onChange={(value) => handleSetRows('onOff', value)}
                            size="small"
                        >
                            <Select.Option value={1}>On</Select.Option>
                            <Select.Option value={0}>Off</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="패키지 운영시간" style={{marginBottom: 8, width: '30%'}}>
                        <Input
                            type="number"
                            name={"packageTime"}
                            value={row.packageTime}
                            onChange={(e) => handleSetRows('packageTime', e.target.value < 0 ? 0 : e.target.value)}
                            size="small"
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="메모" style={{marginBottom: 8, width: '60%'}}>
                        <Input
                            name={"memo"}
                            value={row.memo}
                            onChange={(e) => handleSetRows('memo', e.target.value)}
                            size="small"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    {/*<Form.Item label="반복 판매 간격" style={{marginBottom: 8, width: '40%'}}>*/}
                    {/*    <InputNumber*/}
                    {/*        min={1}*/}
                    {/*        value={row.intervalValue}*/}
                    {/*        onChange={val => handleSetRows('intervalValue', val)}*/}
                    {/*        size="small"*/}
                    {/*        style={{ width: '60%' }}*/}
                    {/*    />*/}
                    {/*    <Select*/}
                    {/*        value={row.intervalUnit}*/}
                    {/*        onChange={val => handleSetRows('intervalUnit', val)}*/}
                    {/*        size="small"*/}
                    {/*        style={{ width: '38%', marginLeft: '2%' }}*/}
                    {/*    >*/}
                    {/*        {intervalUnitOptions.map(opt => (*/}
                    {/*            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>*/}
                    {/*        ))}*/}
                    {/*    </Select>*/}
                    {/*</Form.Item>*/}
                    <Form.Item label="미니게임 인덱스" style={{width: '35%'}}>
                        <Select
                            onChange={(e) => handleSetRows('miniGameIdx', e)}
                            size="small"
                            className="name-select"
                            showSearch
                            optionLabelProp="label"
                            value={row.miniGameIdx}
                        >
                            {miniGameIndexList.map((option, index) => (
                                <Select.Option
                                    key={index}
                                    value={option.index}
                                    label={option.index}
                                >
                                    <div>
                                        <div style={{fontSize: '11px', color: '#504f4f'}}>
                                            ID: {option.index}
                                        </div>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="미니게임 메모" style={{width: '35%'}}>
                        {miniGame.find(game => game.index === row.miniGameIdx)?.memo || ''}
                    </Form.Item>
                </Form>
            </div>
            <CustomDateTimePicker rows={row} handleSetRows={handleSetRows} hasAdd={false}/>
            <DateStepper setRows={setRow} entity={CharacterPackageEntity}/>
            <div className={"event-edit-buttons"}>
                {/*<Button type="dashed" onClick={handleRegisterRepeatEvent} style={{marginRight: 8}}>*/}
                {/*    반복 상품 등록*/}
                {/*</Button>*/}
                <CopyButton onClick={handleCopy} handleSetDialog={handleSetDialog}/>
                <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                <CancelButton onClick={handleClose}/>
                <DeleteButton onClick={handleDelete} handleSetDialog={handleSetDialog} display={false}/>
            </div>
        </div>
    );
}

export default CharacterPackageEditModal;
